import { useRef, useEffect, useState } from 'react';
import cn from 'classnames';

import useIsScreenSize from 'js/hooks/use-is-screen-size';
import { backgrounds } from 'js/utils/np-colors';
import doAndWait from 'js/utils/do-and-wait';

import Icon from 'components/icon/Icon';
import Button from 'components/button/Button';
import VideoBlock from 'components/nysgjerrigper/video-block/VideoBlock';
import StepsBlock from 'components/nysgjerrigper/steps-block/StepsBlock';
import QuotesBlock from 'components/nysgjerrigper/quotes-block/QuotesBlock';

import { IStepsContent } from 'source/NFR.types';

type ComponentName = 'videoBlock' | 'stepsBlock' | 'quotesBlock';

interface StepsContentProps extends IStepsContent {
  onClose: () => void;
}

const StepsContent: React.FC<StepsContentProps> = ({ close, onClose, activeStepIndex, steps = [] }) => {
  const [isActive, setIsActive] = useState(activeStepIndex > -1);
  const [scrollPos, setScrollPos] = useState<number[]>([0, 0, 0, 0, 0, 0]);
  const [activeStepIndexState, setActiveStepIndexState] = useState(activeStepIndex > -1 ? activeStepIndex : 0);
  const [isMounted, setIsMounted] = useState(false);
  const stepsContentRef = useRef<HTMLDivElement>(null);
  const isDesktop = useIsScreenSize('md');

  useEffect(() => {
    setIsMounted(true);
    const unselected = activeStepIndex === -1;
    const previousUnselected = !isActive;

    if (unselected) {
      setIsActive(false);
    } else if (previousUnselected) {
      setActiveStepIndexState(activeStepIndex);
      setIsActive(true);
    } else {
      const switchOverlay = async () => {
        await doAndWait(() => {
          setIsActive(false);
        }, 500);
        setActiveStepIndexState(activeStepIndex);
        setIsActive(true);
      };
      void switchOverlay();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStepIndex]);

  useEffect(() => {
    if (!stepsContentRef.current) return;

    if (isActive) {
      stepsContentRef.current.scrollTo(0, scrollPos[activeStepIndex]);
    } else {
      setScrollPos(state => [
        ...state.slice(0, activeStepIndexState),
        stepsContentRef.current!.scrollTop,
        ...state.slice(activeStepIndexState + 1)
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive, activeStepIndex, activeStepIndexState]);

  const getBackgroundColor = (index: number, blockName: ComponentName) => {
    switch (blockName) {
      case 'stepsBlock':
      case 'quotesBlock':
        return { background: 'white' };
      default:
        return backgrounds(index);
    }
  };

  // Type-safe render function for each block type
  const renderBlock = (blockName: ComponentName, blockIndex: number) => {
    const activeStepBlocks = steps[activeStepIndexState];
    if (!activeStepBlocks) return null;

    switch (blockName) {
      case 'videoBlock':
        if (!activeStepBlocks.videoBlock) return null;
        return <VideoBlock {...activeStepBlocks.videoBlock} />;

      case 'stepsBlock':
        if (!activeStepBlocks.stepsBlock) return null;
        return <StepsBlock {...activeStepBlocks.stepsBlock} stepIndex={activeStepIndexState} />;

      case 'quotesBlock':
        if (!activeStepBlocks.quotesBlock) return null;
        return (
          <QuotesBlock
            {...activeStepBlocks.quotesBlock}
            blockIndex={blockIndex}
            stepIndex={activeStepIndexState}
            stepsContentNode={stepsContentRef.current}
          />
        );

      default:
        return null;
    }
  };

  const activeStepBlocks = isActive && steps[activeStepIndexState];

  return (
    <div
      className={cn('steps-content', { 'steps-content--is-active': isActive })}
      ref={stepsContentRef}
      style={isMounted ? { transform: 'translateX(100%)' } : undefined}
    >
      {isDesktop ? (
        <Button
          className="steps-content--close steps-content--close--desktop"
          onClick={onClose}
          icon="np-close"
          iconFill
        >
          {close.textDesktop}
        </Button>
      ) : (
        <Button
          className="steps-content--close steps-content--close--mobile"
          style={backgrounds(activeStepIndexState)}
          onClick={onClose}
        >
          <Icon name="link-arrow" /> {close.textMobile}
        </Button>
      )}

      {activeStepBlocks &&
        (Object.keys(activeStepBlocks) as ComponentName[]).map((blockName, blockIndex) => {
          const content = renderBlock(blockName, blockIndex);
          if (!content) return null;

          return (
            <div
              className="steps-content--component-wrapper"
              key={blockName + blockIndex}
              style={getBackgroundColor(activeStepIndexState + blockIndex, blockName)}
            >
              {content}
            </div>
          );
        })}
    </div>
  );
};

StepsContent.displayName = 'StepsContent';

export default StepsContent;
